import { assign } from 'lodash';

import { iaRa, io, spaceToKebabCase } from 'lib/helpers/utility';

import {
  processAdvancedHPForms,
  processAdvancedSOAPForms
} from 'components/practice/charts/ClinicalNote/Orders/lib/clinicalSummaryHelpers';
import { filterAndApplyPositions } from 'components/practice/charts/ClinicalNote/PreviewNote/Selected/lib/filterAndApplyPositions';
import { selectedInitialState } from 'components/practice/charts/ClinicalNote/PreviewNote/lib/initials';
import {
  mapRenderedInitialState,
  processSelectedForms
} from 'components/practice/charts/ClinicalNote/PreviewNote/lib/renderedHelper';
import {
  prepareSelection,
  processAdvancedCustomForms
} from 'components/practice/charts/ClinicalNote/PreviewNote/lib/selectedHelper';
import { processCustomFormTypes } from 'components/practice/charts/ClinicalNote/lib/processCustomNoteTypes';

const { getAttachments } = require('api/Attachments');
const { getLabOrder } = require('api/LabOrder');
const { getMyScribe } = require('api/MyScribe');
const { getNonERXPrescription } = require('api/NonERXPrescription');
const { getReferralOrder } = require('api/ReferalOrder');

export const fetchAdditionalNoteData = async (navigate, appointmentId, patientId) => {
  const [attachments, myScribe, referralOrder, nonERXPrescription, labOrder] = await Promise.all([
    getAttachments(navigate, { appointmentId, patientId }),
    getMyScribe(navigate, { appointmentId }),
    getReferralOrder(navigate, { clinicalNoteIds: [appointmentId] }),
    getNonERXPrescription(navigate, { appointmentIds: [appointmentId] }),
    getLabOrder(navigate, {
      appointmentIds: [appointmentId],
      withRelations: { provider: true, tests: true }
    })
  ]);

  return {
    attachments,
    myScribe,
    referralOrder,
    nonERXPrescription,
    labOrder
  };
};

export const processFormData = (result, cnDisplaySettings) => {
  const { clinicalNote, advancedHP, advancedSOAP, customFormTypes, serviceCPT } = result;

  const processedAdvancedHP = iaRa(advancedHP).map((item) => ({
    ...item,
    custom_title: `${spaceToKebabCase(item?.form?.name || item?.name)}-${item?.form_id || item?.id}`
  }));
  const processedAdvancedSOAP = iaRa(advancedSOAP).map((item) => ({
    ...item,
    custom_title: `${spaceToKebabCase(item?.form?.name || item?.name)}-${item?.form_id || item?.id}`
  }));

  const cpt_codes = iaRa(serviceCPT);

  const processedCustomFormTypes = processCustomFormTypes({ customFormTypes });
  const getAdvancedHP = processAdvancedHPForms(processedAdvancedHP, cnDisplaySettings);
  const getAdvancedSOAP = processAdvancedSOAPForms(processedAdvancedSOAP, cnDisplaySettings);
  const processedAdvancedForms = Object.entries(customFormTypes || {}).map(([, object]) => {
    return processAdvancedCustomForms(object, cnDisplaySettings);
  });
  const getAdvancedFroms = assign({}, ...processedAdvancedForms);

  return {
    clinicalNote,
    advancedHP: processedAdvancedHP,
    advancedSOAP: processedAdvancedSOAP,
    customFormTypes: processedCustomFormTypes,
    processedAdvancedHP: getAdvancedHP,
    processedAdvancedSOAP: getAdvancedSOAP,
    processedAdvancedForms: getAdvancedFroms,
    cpt_codes
  };
};

export const buildSelectionObject = (
  processedData,
  additionalData,
  cnDisplaySettings,
  currentHpOverviewData,
  overviewData
) => {
  const selectedObj = {
    ...selectedInitialState(
      processedData?.clinicalNote,
      cnDisplaySettings,
      currentHpOverviewData,
      null,
      iaRa(additionalData?.myScribe?.data?.myscribe),
      iaRa(additionalData?.referalOrder?.data?.referrals),
      iaRa(additionalData?.nonERXPrescription?.data?.nonERXPrescription),
      iaRa(additionalData?.labOrder?.data?.labOrder),
      overviewData
    ),
    ...processedData.processedAdvancedHP,
    ...processedData.processedAdvancedSOAP,
    ...processedData.processedAdvancedForms
  };

  let componentsOrder = null;
  const selectedCustomFormTypes = cnDisplaySettings?.todaysNote;

  if (processedData.clinicalNote?.locked && io(processedData.clinicalNote?.components_order)) {
    componentsOrder = processedData.clinicalNote?.components_order;

    const checkIFNewVersion = Object.keys(componentsOrder).every((key) => {
      return !!componentsOrder[key]?.id || !!componentsOrder[key]?.title;
    });

    if (!checkIFNewVersion) {
      componentsOrder = filterAndApplyPositions(selectedObj, componentsOrder);
    }
  }

  const clinicalSummary = null;

  if (io(clinicalSummary)) {
    const result = prepareSelection({
      savedSelection: { ...selectedObj, ...clinicalSummary },
      initialSelection: selectedObj,
      componentsOrder,
      selectedCustomFormTypes,
      attachments: additionalData?.attachments?.documents
    });

    return result;
  } else {
    const formattedSelect = prepareSelection({
      initialSelection: selectedObj,
      componentsOrder,
      selectedCustomFormTypes,
      attachments: additionalData?.attachments?.documents
    });
    return formattedSelect;
  }
};

export const prepareComponentProps = (selected, clinicalNote) => {
  const rendered = mapRenderedInitialState(selected);

  const { checkedRendered, hpForms, soapForms, advancedForms } = processSelectedForms({
    selected,
    rendered,
    componentsOrder: clinicalNote?.locked ? clinicalNote?.components_order : null
  });

  return { checkedRendered, hpForms, soapForms, advancedForms };
};
